import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import { useAlert } from '@hooks/useAlert';
import { useNameMapQuery } from '@features/redux/apis/org';
import { setLastUpdated } from '@features/redux/slices/reportSlice';
import {
    Box,
    Typography,
    useMediaQuery,
    useTheme,
    Pagination,
    MenuItem,
    Select,
    TextField as MuiTextField,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { BasePage } from './bases/BasePage';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { useOrgs } from '@hooks/useOrgs';
import { useScrollablePagination } from '@hooks/useScrollablePagination';
import { MobileEmployeeCard, MobileEmployeeCardSkeleton } from '@components/MobileEmployeeCard';
import { TotalCuts } from '@components/reports/today/TotalCuts';
import { CustomDatePicker } from '@components/CustomDatePicker';
import { type EmployeeResponse, fetchOrgData, OrgUrls } from '@api/org';
import { MaterialIdMap } from '@consts/materials';
import { EmployeeExport } from '@components/reports/employees/EmployeeExport';
import { DatePreset, datePresetFns } from '@utils/dates';

import {
    colors,
    typography as typographyTokens,
    radii,
    Button,
    FilterPanel,
    FilterSection,
    TableCard,
    DataTable as DesignDataTable,
    TableLink,
    TextField,
} from '@components/design-system';
import type { DataTableColumn } from '@components/design-system/components/Table/DataTable';
import { MultiSelectFilter } from '@components/MultiSelectFilter';
import { AppRoutes } from '@consts/routes';

const PAGE_SIZE = 50;
const MOBILE_PAGE_SIZE = 20;

type FilterInput = {
    companies: { [key: string]: boolean };
    resellers: { [key: string]: boolean };
    locations: { [key: string]: boolean };
};

const LABEL_MAP: Record<string, string> = {
    warrantyPercentage: 'Warranty %',
    totalCuts: '# Cuts',
    warranty: '# Warranty Cuts',
    successful: '# Successful Cuts',
};

interface EmployeesProps {
    setDateRange: (range: { startDate: string; endDate: string }) => void;
    dateRange: { startDate: string; endDate: string };
}

export const Employees = ({ setDateRange, dateRange }: EmployeesProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const { setAlert } = useAlert();

    const { IdToken } = useSelector((state: RootState) => state.auth);
    const tz = useSelector((state: RootState) => state.timezone.tz);
    const { companies: companiesMap, resellers: resellersMap, locations: locationsMap } = useOrgs();
    const { data: nameMap, isFetching: isLoadingNames } = useNameMapQuery({});

    // Convert IdMap to array format for MultiSelectFilter
    const companies = useMemo(() => Object.entries(companiesMap).map(([id, name]) => ({ id, name })), [companiesMap]);
    const resellers = useMemo(() => Object.entries(resellersMap).map(([id, name]) => ({ id, name })), [resellersMap]);
    const locations = useMemo(() => Object.entries(locationsMap).map(([id, name]) => ({ id, name })), [locationsMap]);

    const [employeesData, setEmployeesData] = useState<{ employees: EmployeeResponse[]; totalRecords: number }>({
        employees: [],
        totalRecords: 0,
    });
    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [filters, setFilters] = useState<FilterInput>({
        companies: {},
        resellers: {},
        locations: {},
    });

    const [draftFilters, setDraftFilters] = useState<FilterInput>({
        companies: {},
        resellers: {},
        locations: {},
    });

    const [pageNum, setPageNum] = useState<number>(1);
    const [searchText, setSearchText] = useState<string>('');
    const [draftSearchText, setDraftSearchText] = useState<string>('');
    const [rankBy, setRankBy] = useState<'totalCuts' | 'warranty' | 'successful' | 'warrantyPercentage'>('totalCuts');
    const [material, setMaterial] = useState<keyof typeof MaterialIdMap | 'All'>('All');
    const { scrollContainerRef, scrollToTop } = useScrollablePagination();

    const handlePageChange = useCallback(
        (newPage: number) => {
            setPageNum(newPage);
            scrollToTop();
        },
        [scrollToTop]
    );

    const pageSize = isMobile ? MOBILE_PAGE_SIZE : PAGE_SIZE;

    const getEmployeesData = async () => {
        setLoadingData(true);
        try {
            const ranks = await fetchOrgData<{ employees: EmployeeResponse[]; totalRecords: number }>(
                IdToken!,
                OrgUrls.LocationEmployees,
                {
                    pageSize,
                    pageNum: pageNum - 1,
                    companyIds: Object.keys(filters.companies)
                        .filter((key) => filters.companies[key])
                        .join(','),
                    resellerIds: Object.keys(filters.resellers)
                        .filter((key) => filters.resellers[key])
                        .join(','),
                    locationIds: Object.keys(filters.locations)
                        .filter((key) => filters.locations[key])
                        .join(','),
                    rankBy,
                    searchText,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    material: material === 'All' ? undefined : material,
                },
                tz,
            );
            dispatch(setLastUpdated());
            setEmployeesData(ranks);
        } catch (e) {
            setAlert('Could not get employee ranking', 'error');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        getEmployeesData();
    }, [pageNum, filters, searchText, rankBy, material, dateRange, tz]);

    const totalRecords = employeesData.totalRecords;
    const numPages = useMemo(() => Math.ceil(totalRecords / pageSize) || 1, [totalRecords, pageSize]);

    const handleOpenFilters = () => {
        setDraftFilters(filters);
        setDraftSearchText(searchText);
        setFiltersOpen(true);
    };

    const handleCloseFilters = () => {
        // Apply filters when closing the drawer
        setFilters(draftFilters);
        setSearchText(draftSearchText);
        setPageNum(1);
        setFiltersOpen(false);
    };

    const handleResetFilters = () => {
        // Reset all filters and apply immediately
        setDraftFilters({ companies: {}, resellers: {}, locations: {} });
        setDraftSearchText('');
        setFilters({ companies: {}, resellers: {}, locations: {} });
        setSearchText('');
        setRankBy('totalCuts');
        setMaterial('All');
        setDateRange(datePresetFns[DatePreset.LastSevenDays]());
        setPageNum(1);
    };

    const infoText =
        totalRecords > 0
            ? `Showing ${Math.min((pageNum - 1) * pageSize + 1, totalRecords)}-${Math.min(
                  pageNum * pageSize,
                  totalRecords
              )} of ${totalRecords} employees`
            : 'No employees found';

    // Table columns for desktop
    const columns: DataTableColumn<EmployeeResponse>[] = [
        {
            field: 'EmployeeRank',
            headerName: 'Rank',
            width: 70,
            align: 'center',
        },
        {
            field: 'Name',
            headerName: 'Name',
            flex: 1,
            minWidth: 150,
            renderCell: ({ row }) => (
                <TableLink
                    to={`${AppRoutes.EmployeeDetails}?startDate=${encodeURIComponent(
                        dateRange.startDate
                    )}&endDate=${encodeURIComponent(dateRange.endDate)}&email=${encodeURIComponent(
                        row.Email
                    )}&locationId=${encodeURIComponent(row.LocationID)}`}>
                    {row.Name}
                </TableLink>
            ),
        },
        {
            field: 'Email',
            headerName: 'Email',
            flex: 1,
            minWidth: 180,
        },
        {
            field: 'CompanyID',
            headerName: 'Company',
            flex: 1,
            minWidth: 120,
            renderCell: ({ row }) => nameMap?.companies[row.CompanyID] || 'Unknown',
        },
        {
            field: 'ResellerID',
            headerName: 'Reseller',
            flex: 1,
            minWidth: 120,
            renderCell: ({ row }) => nameMap?.resellers[row.ResellerID] || 'None',
        },
        {
            field: 'LocationID',
            headerName: 'Location',
            flex: 1,
            minWidth: 120,
            renderCell: ({ row }) => nameMap?.locations[row.LocationID] || 'Unknown',
        },
        {
            field: 'TotalCuts',
            headerName: '# Cuts',
            width: 90,
            align: 'center',
        },
        {
            field: 'WarrantyClaimPercentage',
            headerName: 'Warranty %',
            width: 100,
            align: 'center',
        },
        {
            field: 'SuccessfulCuts',
            headerName: 'Successful',
            width: 130,
            renderCell: ({ row }) => (
                <TotalCuts totalCuts={row.TotalCuts} numCutType={row.SuccessfulCuts} color='success' />
            ),
        },
        {
            field: 'WarrantyClaimCuts',
            headerName: 'Warranty',
            width: 130,
            renderCell: ({ row }) => (
                <TotalCuts totalCuts={row.TotalCuts} numCutType={row.WarrantyClaimCuts} color='error' />
            ),
        },
    ];

    const noResultsText = (
        <Typography>
            None of your companies have the &quot;Sales Rep Tracking&quot; feature enabled.
            <br />
            Please contact support at <b>partner@moutfitters.com</b> if you wish to opt into the feature.
        </Typography>
    );

    // Mobile view
    if (isMobile) {
        return (
            <BasePage>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}>
                    {/* Row 1: Title + Filter button */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            pt: 2,
                            pb: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                color: colors.neutral[900],
                            }}>
                            Employee Reports
                        </Typography>
                        <Button
                            variant='secondary'
                            size='small'
                            onClick={handleOpenFilters}
                            startIcon={<FilterListIcon />}
                            sx={{
                                minWidth: 'auto',
                                px: 1,
                                '& .MuiButton-startIcon': { margin: 0 },
                            }}>
                            {''}
                        </Button>
                    </Box>

                    {/* Row 2: Info text + Export button */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: '0.8125rem',
                                color: colors.neutral[600],
                            }}>
                            {totalRecords > 0 ? infoText : 'No employees found'}
                        </Typography>
                        <EmployeeExport
                            filters={filters}
                            searchText={searchText}
                            rankBy={rankBy}
                            material={material}
                            dateRange={dateRange}
                        />
                    </Box>

                    {/* Cards Container */}
                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            p: 1.5,
                            backgroundColor: colors.neutral[50],
                        }}>
                        {loadingData || isLoadingNames ? (
                            Array.from({ length: MOBILE_PAGE_SIZE }).map((_, i) => (
                                <MobileEmployeeCardSkeleton key={i} />
                            ))
                        ) : employeesData.employees.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    color: colors.neutral[500],
                                    textAlign: 'center',
                                    p: 2,
                                }}>
                                {noResultsText}
                            </Box>
                        ) : (
                            employeesData.employees.map((employee, idx) => (
                                <MobileEmployeeCard
                                    key={`${employee.Email}-${employee.LocationID}-${idx}`}
                                    data={employee}
                                    dateRange={dateRange}
                                    companyName={nameMap?.companies[employee.CompanyID]}
                                    resellerName={nameMap?.resellers[employee.ResellerID]}
                                    locationName={nameMap?.locations[employee.LocationID]}
                                />
                            ))
                        )}
                    </Box>

                    {/* Mobile Pagination */}
                    {numPages > 1 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 1.5,
                                backgroundColor: colors.neutral[0],
                                borderTop: `1px solid ${colors.neutral[200]}`,
                            }}>
                            <Pagination
                                count={numPages}
                                page={pageNum}
                                onChange={(_, page) => handlePageChange(page)}
                                color='primary'
                                size='small'
                                siblingCount={0}
                                boundaryCount={1}
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        borderRadius: `${radii.md}px`,
                                        minWidth: 32,
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {/* Filter Panel */}
                    <FilterPanel open={filtersOpen} onClose={handleCloseFilters} onReset={handleResetFilters}>
                        <FilterSection title='Date Range'>
                            <CustomDatePicker dateRange={dateRange} setDateRange={setDateRange} />
                        </FilterSection>
                        <FilterSection title='Search'>
                            <TextField
                                label='Employee Name or Email'
                                value={draftSearchText}
                                onChange={setDraftSearchText}
                                placeholder='Search employees...'
                            />
                        </FilterSection>
                        <FilterSection title='Rank By'>
                            <Select
                                value={rankBy}
                                onChange={(e) => setRankBy(e.target.value as any)}
                                fullWidth
                                size='small'
                                IconComponent={KeyboardArrowDownIcon}
                                sx={{ fontSize: typographyTokens.fontSize.sm }}>
                                {Object.keys(LABEL_MAP).map((key) => (
                                    <MenuItem key={key} value={key}>
                                        {LABEL_MAP[key]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FilterSection>
                        <FilterSection title='Material'>
                            <Select
                                value={material}
                                onChange={(e) => setMaterial(e.target.value as any)}
                                fullWidth
                                size='small'
                                IconComponent={KeyboardArrowDownIcon}
                                sx={{ fontSize: typographyTokens.fontSize.sm }}>
                                <MenuItem value='All'>All Materials</MenuItem>
                                {Object.keys(MaterialIdMap).map((key) => (
                                    <MenuItem key={key} value={key}>
                                        {(MaterialIdMap as any)[key][0]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FilterSection>
                        <FilterSection title='Organization'>
                            <MultiSelectFilter
                                label='Company'
                                options={companies}
                                value={draftFilters.companies}
                                onChange={(company) => {
                                    const companyObj = company as { id: string; name: string };
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        companies: {
                                            ...prev.companies,
                                            [companyObj.id]: !prev.companies[companyObj.id],
                                        },
                                    }));
                                }}
                                getLabelValue={(opt) => opt.name}
                                idKey='id'
                                search
                                margin={false}
                            />
                            <MultiSelectFilter
                                label='Reseller'
                                options={resellers}
                                value={draftFilters.resellers}
                                onChange={(reseller) => {
                                    const resellerObj = reseller as { id: string; name: string };
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        resellers: {
                                            ...prev.resellers,
                                            [resellerObj.id]: !prev.resellers[resellerObj.id],
                                        },
                                    }));
                                }}
                                getLabelValue={(opt) => opt.name}
                                idKey='id'
                                search
                                margin={false}
                            />
                            <MultiSelectFilter
                                label='Location'
                                options={locations}
                                value={draftFilters.locations}
                                onChange={(location) => {
                                    const locationObj = location as { id: string; name: string };
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        locations: {
                                            ...prev.locations,
                                            [locationObj.id]: !prev.locations[locationObj.id],
                                        },
                                    }));
                                }}
                                getLabelValue={(opt) => opt.name}
                                idKey='id'
                                search
                                margin={false}
                            />
                        </FilterSection>
                    </FilterPanel>
                </Box>
            </BasePage>
        );
    }

    // Desktop view
    return (
        <BasePage>
            <BreadcrumbSubBar />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: colors.neutral[100],
                    p: 2,
                }}>
                <TableCard
                    title='Employee Reports'
                    actions={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <MuiTextField
                                placeholder='Search by name or email...'
                                value={searchText}
                                onChange={(e) => {
                                    setSearchText(e.target.value);
                                    setPageNum(1);
                                }}
                                size='small'
                                InputProps={{
                                    endAdornment: <SearchIcon sx={{ color: colors.neutral[400], fontSize: 20 }} />,
                                }}
                                sx={{
                                    minWidth: 220,
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: typographyTokens.fontSize.sm,
                                        borderRadius: `${radii.md}px`,
                                    },
                                }}
                            />
                            <EmployeeExport
                                filters={filters}
                                searchText={searchText}
                                rankBy={rankBy}
                                material={material}
                                dateRange={dateRange}
                            />
                            <Button
                                variant='secondary'
                                size='small'
                                onClick={handleOpenFilters}
                                startIcon={<FilterListIcon />}>
                                Filters
                            </Button>
                        </Box>
                    }
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {/* Table */}
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <DesignDataTable
                            rows={employeesData.employees}
                            columns={columns}
                            getRowId={(row) => `${row.Email}-${row.LocationID}`}
                            totalRows={totalRecords}
                            page={pageNum}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            loading={loadingData || isLoadingNames}
                            height='100%'
                            infoText={infoText}
                        />
                    </Box>
                </TableCard>

                {/* Filter Panel */}
                <FilterPanel open={filtersOpen} onClose={handleCloseFilters} onReset={handleResetFilters}>
                    <FilterSection title='Date Range'>
                        <CustomDatePicker dateRange={dateRange} setDateRange={setDateRange} />
                    </FilterSection>
                    <FilterSection title='Ranking Options'>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.xs,
                                        fontWeight: typographyTokens.fontWeight.medium,
                                        color: colors.neutral[600],
                                        mb: 0.5,
                                    }}>
                                    Rank By
                                </Typography>
                                <Select
                                    value={rankBy}
                                    onChange={(e) => setRankBy(e.target.value as any)}
                                    size='small'
                                    fullWidth
                                    IconComponent={KeyboardArrowDownIcon}
                                    sx={{ fontSize: typographyTokens.fontSize.sm }}>
                                    {Object.keys(LABEL_MAP).map((key) => (
                                        <MenuItem key={key} value={key}>
                                            {LABEL_MAP[key]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.xs,
                                        fontWeight: typographyTokens.fontWeight.medium,
                                        color: colors.neutral[600],
                                        mb: 0.5,
                                    }}>
                                    Material
                                </Typography>
                                <Select
                                    value={material}
                                    onChange={(e) => setMaterial(e.target.value as any)}
                                    size='small'
                                    fullWidth
                                    IconComponent={KeyboardArrowDownIcon}
                                    sx={{ fontSize: typographyTokens.fontSize.sm }}>
                                    <MenuItem value='All'>All Materials</MenuItem>
                                    {Object.keys(MaterialIdMap).map((key) => (
                                        <MenuItem key={key} value={key}>
                                            {(MaterialIdMap as any)[key][0]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>
                    </FilterSection>
                    <FilterSection title='Organization'>
                        <MultiSelectFilter
                            label='Company'
                            options={companies}
                            value={draftFilters.companies}
                            onChange={(company) => {
                                const companyObj = company as { id: string; name: string };
                                setDraftFilters((prev) => ({
                                    ...prev,
                                    companies: { ...prev.companies, [companyObj.id]: !prev.companies[companyObj.id] },
                                }));
                            }}
                            getLabelValue={(opt) => opt.name}
                            idKey='id'
                            search
                            margin={false}
                        />
                        <MultiSelectFilter
                            label='Reseller'
                            options={resellers}
                            value={draftFilters.resellers}
                            onChange={(reseller) => {
                                const resellerObj = reseller as { id: string; name: string };
                                setDraftFilters((prev) => ({
                                    ...prev,
                                    resellers: { ...prev.resellers, [resellerObj.id]: !prev.resellers[resellerObj.id] },
                                }));
                            }}
                            getLabelValue={(opt) => opt.name}
                            idKey='id'
                            search
                            margin={false}
                        />
                        <MultiSelectFilter
                            label='Location'
                            options={locations}
                            value={draftFilters.locations}
                            onChange={(location) => {
                                const locationObj = location as { id: string; name: string };
                                setDraftFilters((prev) => ({
                                    ...prev,
                                    locations: { ...prev.locations, [locationObj.id]: !prev.locations[locationObj.id] },
                                }));
                            }}
                            getLabelValue={(opt) => opt.name}
                            idKey='id'
                            search
                            margin={false}
                        />
                    </FilterSection>
                </FilterPanel>
            </Box>
        </BasePage>
    );
};
